/**
 * AppBarSlotContext — 페이지가 앱바의 **제목과 우측 액션**을 대신 정하게 해준다.
 *
 * 앱바는 Layout 이 그리고 페이지는 그 자식이라, 라우트 `handle` 에 박아둔 고정
 * 문자열 말고는 페이지가 앱바에 말을 걸 방법이 없었다. 카드 상세처럼 제목이
 * **데이터에 달린**(카드명) 화면과, 페이지 고유 액션(공유)을 앱바로 올려야 하는
 * 화면을 위해 통로를 하나 낸다.
 *
 * 쓰는 쪽:
 *   useAppBarSlot(card?.cardNm, shareAction);   // 마운트 동안만 적용, 나가면 원복
 *
 * ⚠ `action` 은 **메모이즈된 노드**를 넘겨야 한다. 매 렌더 새 JSX 를 만들면
 * effect 가 매번 돌아 setState 루프가 된다.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface AppBarSlot {
  /** 라우트 handle 의 appBarTitle 을 덮어쓸 제목 */
  title?: string;
  /** 앱바 우측에 붙일 액션 (아이콘 버튼 등) */
  action?: ReactNode;
  /**
   * 슬롯을 채운 화면이 속한 activity id (`AppBarSlotOwner` 가 주입). 슬롯은 하나뿐인데
   * activity 는 겹쳐 뜨고 **아래 화면도 마운트된 채**라, 소유자를 안 밝히면 카드 상세의
   * 카드명·공유가 그 위에 열린 공유 화면 앱바에 그대로 새어나온다. 앱바는 자기 소유
   * 슬롯만 쓴다. 메인 라우트 페이지(Layout 앱바)는 undefined.
   */
  owner?: string;
}

interface AppBarSlotContextValue extends AppBarSlot {
  setSlot: (slot: AppBarSlot) => void;
}

const AppBarSlotContext = createContext<AppBarSlotContextValue | null>(null);

/** 현재 subtree 를 소유한 activity id. ActivityHost 밖(메인 라우트)에서는 undefined. */
const AppBarSlotOwnerContext = createContext<string | undefined>(undefined);

/** ActivityHost 가 각 activity subtree 를 감싸 소유자를 알린다. */
export function AppBarSlotOwner({ id, children }: { id: string; children: ReactNode }) {
  return (
    <AppBarSlotOwnerContext.Provider value={id}>{children}</AppBarSlotOwnerContext.Provider>
  );
}

export function AppBarSlotProvider({ children }: { children: ReactNode }) {
  const [slot, setSlotState] = useState<AppBarSlot>({});
  const setSlot = useCallback((next: AppBarSlot) => setSlotState(next), []);
  const value = useMemo(
    () => ({ title: slot.title, action: slot.action, owner: slot.owner, setSlot }),
    [slot.title, slot.action, slot.owner, setSlot],
  );
  return <AppBarSlotContext.Provider value={value}>{children}</AppBarSlotContext.Provider>;
}

/**
 * 앱바가 읽는 쪽. Provider 밖이면 빈 슬롯 — 앱바는 handle 값을 그대로 쓴다.
 *
 * `owner` 를 넘기면 그 소유자의 슬롯일 때만 값이 나온다 (ActivityHost 는 activity id,
 * Layout 은 생략 = 메인 라우트 소유 슬롯만).
 */
export function useAppBarSlotValue(owner?: string): AppBarSlot {
  const ctx = useContext(AppBarSlotContext);
  if (!ctx || ctx.owner !== owner) return EMPTY_SLOT;
  return { title: ctx.title, action: ctx.action };
}

const EMPTY_SLOT: AppBarSlot = {};

/**
 * 페이지가 쓰는 쪽. 마운트/변경 시 슬롯을 채우고 언마운트 시 비운다.
 * 비우지 않으면 다음 화면이 앞 화면의 제목·액션을 물려받는다.
 */
export function useAppBarSlot(title?: string, action?: ReactNode): void {
  const ctx = useContext(AppBarSlotContext);
  const owner = useContext(AppBarSlotOwnerContext);
  const setSlot = ctx?.setSlot;
  useEffect(() => {
    if (!setSlot) return;
    setSlot({ title, action, owner });
    return () => setSlot({});
  }, [setSlot, title, action, owner]);
}
